import React, { FunctionComponent, useState } from 'react'
import { Avatar, Button, Modal, Text } from '@afterpaytouch/core'
import { useAppDispatch, useAppRouter, useConsumerName, useConsumerSelector, authLogout } from '../../state'
import { Route } from '../../config/router'
import { useTranslation } from '../../utils/convergence'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useCheckoutSizes } from '../../utils/responsive'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import styles from './style.module.css'

interface AccountModalProps {
  show: boolean
  onClose: () => void
}

/**
 * CASHDAP-3225: account half-sheet (Variant A) for a recognized Auto Remember Me session. Opened from
 * the profile-avatar chip in the header. Shows the consumer (avatar + name + email), the remember-me
 * implication, and a "Log out" action that ends the session (existing authLogout — clears the cookie
 * server-side) and returns to the login flow.
 */
export const AccountModal: FunctionComponent<AccountModalProps> = ({ show, onClose }) => {
  const { t } = useTranslation()
  const consumerName = useConsumerName()
  const { email } = useConsumerSelector()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { textSize, headingSize, smallTextSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const onLogout = async (): Promise<void> => {
    setIsLoggingOut(true)
    logEvent(TrackingEvent.PRESSED_AUTO_REMEMBER_ME_ACCOUNT_MODAL_LOG_OUT_BUTTON)
    await dispatch(authLogout())
    router.push(Route.ENTER_EMAIL)
  }

  if (!show) {
    return null
  }

  return (
    <Modal show={show} onClose={onClose} showCloseButton>
      <TrackOnMount eventName={TrackingEvent.VIEWED_AUTO_REMEMBER_ME_ACCOUNT_MODAL} />
      <Modal.Content>
        <div className={styles.content} data-testid='auto-remember-me-account-modal'>
          <MaskedElement actionName='Auto Remember Me Account Modal'>
            <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
            {/* id='modal-label' gives the dialog its accessible name (core Modal sets
                aria-labelledby='modal-label'): the consumer name is the sheet's de-facto title, so
                screen readers announce it instead of landing in an unlabeled dialog. */}
            <Text id='modal-label' size={headingSize} bold>
              {consumerName}
            </Text>
            <Text size={textSize}>{email}</Text>
          </MaskedElement>
          <Text size={smallTextSize}>{t('common:rememberMeAccountModalImplication')}</Text>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button kind='Primary' padding='Fluid' size='Large' onClick={onLogout} loading={isLoggingOut} testNameSpace='auto-remember-me-account-modal-logout'>
          {t('common:rememberMeAccountModalLogOut')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
